import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { personalAccessTokenContract } from "@common/contracts/personal-access-token.contract";
import type {
    PersonalAccessToken,
    CreatePersonalAccessTokenInput,
    CreatePersonalAccessTokenResponse,
} from "@common/schemas/domains/personalAccessToken/personal-access-token.schema";

const patsClient = getBackendAPIClient(personalAccessTokenContract);

const patQueryKeys = {
    list: () => ["personal-access-tokens", "list"] as const,
};

export function usePersonalAccessTokensQuery() {
    return useQuery({
        queryKey: patQueryKeys.list(),
        queryFn: async (): Promise<PersonalAccessToken[]> => {
            const result = await patsClient.list();
            // Guard against non-200 responses (e.g. 404 when the feature
            // flag is disabled) surfacing an empty list silently — without
            // this check `result.body` is the error shape and `.tokens`
            // would be `undefined`.
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load personal access tokens (status ${result.status})`,
                );
            }
            return result.body.tokens;
        },
    });
}

export function useCreatePersonalAccessTokenMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            input: CreatePersonalAccessTokenInput,
        ): Promise<CreatePersonalAccessTokenResponse> => {
            const result = await patsClient.create({ body: input });
            if (result.status !== 201) {
                throw new Error(
                    `Failed to create personal access token (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: patQueryKeys.list(),
            });
        },
    });
}

export function useRevokePersonalAccessTokenMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const result = await patsClient.revoke({
                params: { id },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to revoke personal access token (status ${result.status})`,
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: patQueryKeys.list(),
            });
        },
    });
}
